'use client';

import type { VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';

const gmAwardsButtonVariants = cva(
  'relative flex size-10 shrink-0 items-center justify-center overflow-visible rounded-[0.75rem]',
  {
    variants: {
      state: {
        idle: 'gm-awards-button-no-reward',
        rewardAvailable: 'gm-awards-button-colored',
        gameCenterOpen: 'gm-awards-button-no-reward',
      },
    },
    defaultVariants: {
      state: 'idle',
    },
  },
);

export type GmAwardsButtonState = NonNullable<VariantProps<typeof gmAwardsButtonVariants>['state']>;

interface GmAwardsButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
  VariantProps<typeof gmAwardsButtonVariants> {
  loading?: boolean;
}

export const GmAwardsButton = ({
  className,
  state = 'idle',
  type = 'button',
  loading,
  children,
  ...props
}: GmAwardsButtonProps) => {
  const isGameCenterOpen = state === 'gameCenterOpen';
  const showNotificationDot = state === 'rewardAvailable';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isGameCenterOpen) {
      event.preventDefault();
      return;
    }

    if (props.onClick) {
      props.onClick(event);
    }
  };

  return (
    <button
      type={type}
      className={cn(
        gmAwardsButtonVariants({ state }),
        isGameCenterOpen || loading ? 'cursor-default' : 'cursor-pointer',
        className,
      )}
      onClick={handleClick}
      aria-label="Awards"
      aria-disabled={isGameCenterOpen || loading}
      disabled={isGameCenterOpen || loading}
      {...props}
    >
      <span className="gm-awards-button-surface" aria-hidden />
      <Icon name="reward" className="relative z-1 size-6 text-white" />

      {showNotificationDot && children && (
        <span className="absolute -right-1.75 -top-1.75 z-2 flex h-5 min-w-5 items-center justify-center overflow-hidden rounded-full border-2 border-c900 bg-primary-1 text-[0.625rem] font-medium leading-4 text-white">
          {children}
        </span>
      )}
    </button>
  );
};

export { gmAwardsButtonVariants };
